import React from "react";
import styles from "./admin.module.scss";
import cx from "./cx";
import Icon from "./Icon";

interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  className?: string;
}

/** Page numbers to show, with null for a gap («…»). */
export const pageWindow = (
  page: number,
  count: number,
): Array<number | null> => {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const pages = new Set([1, count, page - 1, page, page + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= count)
    .sort((a, b) => a - b);
  const out: Array<number | null> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(null);
    out.push(p);
  });
  return out;
};

const Pagination: React.FC<PaginationProps> = ({
  page,
  pageCount,
  onChange,
  className,
}) => {
  if (pageCount <= 1) return null;
  return (
    <nav className={cx(styles.pag, className)} aria-label="Sider">
      <button
        type="button"
        className={styles.pagItem}
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Forrige side"
      >
        <Icon name="chevronLeft" size={16} strokeWidth={2} />
      </button>
      {pageWindow(page, pageCount).map((p, i) =>
        p === null ? (
          <span key={`gap-${i}`} className={styles.pagItem} aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={cx(styles.pagItem, p === page && styles.pagOn)}
            aria-current={p === page ? "page" : undefined}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ),
      )}
      <button
        type="button"
        className={styles.pagItem}
        onClick={() => onChange(page + 1)}
        disabled={page >= pageCount}
        aria-label="Neste side"
      >
        <Icon name="chevronRight" size={16} strokeWidth={2} />
      </button>
    </nav>
  );
};

export default Pagination;
